/**
 * General Section - Kiosk Settings
 * CPO Password, Connected Status, Logo URL, Connected Sorting
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Info, Eye, EyeOff } from 'lucide-react';
import { useKioskSettingsStore } from '../../../stores/kiosk-settings-store';
import { MinDimDurationSourceEnum } from '../../../types/kiosk-settings-types';
import { Input } from '@/core/components/ui/input';
import { Label } from '@/core/components/ui/label';
import { Switch } from '@/core/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/core/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/core/components/ui/tooltip';

const CONN_STATUS_SUPPORT_TOOLTIP =
  'When disabled, the following kiosk features are turned off: ' +
  'Start Button on Connection Only, Dynamic Connectors, and First Screen After Plug-In.';

function InfoTooltip({ text }: { text: string }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 cursor-help shrink-0" />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface GeneralSectionProps {
  isOpen: boolean;
  onToggle: () => void;
  hideGracePeriodSource?: boolean;
  hideConnectedStatus?: boolean;
}

export const GeneralSection = ({ isOpen, onToggle, hideGracePeriodSource = false, hideConnectedStatus = false }: GeneralSectionProps) => {
  const { t } = useTranslation('assignEv');
  const { settings, operatorId, updateField, originalSettings } = useKioskSettingsStore();
  const [cpoPasswordInvalid, setCpoPasswordInvalid] = useState(false);
  const [showCpoPassword, setShowCpoPassword] = useState(false);

  useEffect(() => {
    setCpoPasswordInvalid(false);
  }, [originalSettings]);

  if (!settings) return null;

  const isCpoInvalid = cpoPasswordInvalid;

  return (
    <div className="rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      {/* Section Header */}
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between bg-gray-100 hover:bg-gray-200 transition-colors"
      >
        <div className="flex items-center gap-3">
          {isOpen ? (
            <ChevronDown className="w-5 h-5 text-gray-700" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-700" />
          )}
          <h3 className="font-bold text-gray-900 text-base">{t('kiosk.generalSection.title')}</h3>
        </div>
      </button>

      {/* Section Content */}
      {isOpen && (
        <div className="px-4 py-3 bg-white space-y-6">
          {/* Operator ID - Read Only */}
          <div className="w-1/2">
            <Label className="text-xs font-medium text-gray-600 mb-2 block">
              {t('kiosk.generalSection.fields.operatorId')}
            </Label>
            <Input
              value={operatorId || ''}
              disabled
              className="bg-white border-gray-300 text-gray-600"
            />
          </div>

          {/* CPO Password */}
          <div className="w-1/2">
            <Label className="text-xs font-medium text-gray-600 mb-2 block">
              {t('kiosk.generalSection.fields.cpoPassword')}
            </Label>
            <div className="relative">
              <Input
                type={showCpoPassword ? 'text' : 'password'}
                value={settings.cpoPassword || ''}
                onChange={(e) => {
                  const value = e.target.value || null;
                  const isInvalid = value !== null && !/^\d{6,10}$/.test(value);
                  setCpoPasswordInvalid(isInvalid);
                  updateField('cpoPassword', value);
                }}
                placeholder={t('kiosk.generalSection.fields.cpoPasswordPlaceholder')}
                maxLength={10}
                className={`bg-white pr-9 ${isCpoInvalid ? 'border-red-500 focus-visible:ring-red-500' : 'border-gray-300'}`}
              />
              <button
                type="button"
                onClick={() => setShowCpoPassword((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                tabIndex={-1}
              >
                {showCpoPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {isCpoInvalid && <p className="text-xs text-red-500 mt-1">Must be 6-10 digits</p>}
          </div>

          {/* Grace Period Source Field — operator-level only */}
          {!hideGracePeriodSource && <div className="w-1/2">
            <div className="flex items-center gap-1.5 mb-2">
              <Label className="text-xs font-medium text-gray-600">
                {t('kiosk.generalSection.fields.gracePeriodSource')}
              </Label>
              <InfoTooltip text={t('kiosk.generalSection.fields.gracePeriodSourceTooltip')} />
            </div>
            <Select
              value={settings.MinDimDurationSource || MinDimDurationSourceEnum.ABSENT}
              onValueChange={(value) => updateField('MinDimDurationSource', value)}
            >
              <SelectTrigger className="bg-white border-gray-300">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={MinDimDurationSourceEnum.ABSENT}>
                  {t('kiosk.generalSection.fields.gracePeriodAbsent')}
                </SelectItem>
                <SelectItem value={MinDimDurationSourceEnum.EXTERNAL}>
                  {t('kiosk.generalSection.fields.gracePeriodExternal')}
                </SelectItem>
                <SelectItem value={MinDimDurationSourceEnum.MIN_DURATION}>
                  {t('kiosk.generalSection.fields.gracePeriodMinDuration')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>}

          {/* Connected Status Support — operator-level only */}
          {!hideConnectedStatus && (
            <div className="w-1/2">
              <div className="flex items-center gap-1.5 mb-2">
                <Label className="text-xs font-medium text-gray-600">
                  Connected Status Support
                </Label>
                <InfoTooltip text={CONN_STATUS_SUPPORT_TOOLTIP} />
              </div>
              <Switch
                id="connectedStatusSupport"
                checked={settings.connectedStatusSupport !== false}
                onCheckedChange={(checked) => updateField('connectedStatusSupport', checked)}
              />
            </div>
          )}

          {/* Connected Status — operator-level only, disabled when support is off */}
          {!hideConnectedStatus && (
            <div className="w-1/2">
              <div className="flex items-center gap-1.5 mb-2">
                <Label className="text-xs font-medium text-gray-600">
                  {t('kiosk.generalSection.fields.connectedStatus')}
                </Label>
                <InfoTooltip text="Select the status that should be interpreted as 'Connected' in the UI." />
              </div>
              <Select
                value={settings.connected || 'OCPP'}
                onValueChange={(value) => updateField('connected', value)}
                disabled={settings.connectedStatusSupport === false}
              >
                <SelectTrigger className="bg-white border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="OCPP">{t('kiosk.generalSection.fields.connectedOcpp')}</SelectItem>
                  <SelectItem value="Blocked">{t('kiosk.generalSection.fields.connectedBlocked')}</SelectItem>
                  <SelectItem value="Reserved">{t('kiosk.generalSection.fields.connectedReserved')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
